import { z } from 'zod';
import { reasonForAssessmentDictionary } from '../../../dictionary/oasis';
import { getBaseOasisValidationSchema } from '../../oasis';
import { OASIS } from '../../oasis-types';
import { isSubmittedOrApproved } from '../../submitted-or-approved';
import { isTimepoint } from '../util';
/** ICD-10 codes starting with V–Z have no severity rating on SOC, ROC, and recertification. Blank is submitted as ^. */
const BLANK_SEVERITY_TIMEPOINTS = new Set(['soc', 'roc', 'rct']);
export const diagnosisRequiresBlankSeverity = (code, timepoint) => !!timepoint && BLANK_SEVERITY_TIMEPOINTS.has(timepoint) && !!code && /^[vwxyz]/i.test(code);
const severityIsBlank = (severity) => severity == null || severity === '';
const BLANK_SEVERITY_ISSUE = '-5201 Clear severity for codes starting with V, W, X, Y, or Z';
/**
 *
 * Note: if there's an overlapping error for a particular field this is the order in which they'll be displayed
 * form level
 * section level
 * field level
 * base level: same level as "field", but uses built int zod validations
 */
// eslint-disable-next-line
export const sectionIConsistency = ({ input, ctx, version, isSection, field, formStatus }) => {
    const schema = getBaseOasisValidationSchema('e1', 'soc'); // args here doesn't have a purpose
    if (!isSection && !field) {
        // form level
        const payload = input;
        // // I-consistency-5001 If M0100_ASSMT_REASON=[01,03] and if a value submitted in items M1021_PRIMARY_DIAG_ICD, M1023_OTH_DIAG1_ICD, M1023_OTH_DIAG2_ICD, M1023_OTH_DIAG3_ICD, M1023_OTH_DIAG4_ICD, or M1023_OTH_DIAG5_ICD is not equal to [^], it must not equal (must not duplicate) any value submitted in the remaining items in this list.
        // if (isTimepoint([OASIS.SOC, OASIS.ROC], payload.SectionA?.m0100)) {
        // }
        // // I-consistency-5221 If M0100_ASSMT_REASON =[01,03], and if the Primary/Other ICD code in Column 2 is equal to [^], then the corresponding severity code must equal [^].
        // if (isTimepoint([OASIS.SOC, OASIS.ROC], payload.SectionA?.m0100)) {
        // }
        // // I-consistency-5211 If M0100_ASSMT_REASON=[01,03] and if the Primary/Other ICD code in Column 2 is NOT equal to [^] AND character 1 of the ICD code is NOT equal to [V,W,X,Y,Z,v,w,x,y,z], then the corresponding severity code must not equal [^].
        // if (isTimepoint([OASIS.SOC, OASIS.ROC], payload.SectionA?.m0100)) {
        // }
        // Recertification collects the same diagnoses so a HIPPS code can be generated, and uses the same severity rules as SOC/ROC.
        // A V–Z code is the exception: blank severity is valid. Submitted and approved forms keep a severity that was already stored.
        if (isTimepoint([OASIS.SOC, OASIS.ROC, OASIS.RCT], payload.SectionA?.m0100)) {
            const primary = payload.SectionI?.m1021;
            const timepoint = reasonForAssessmentDictionary.find((item) => item.value === payload.SectionA?.m0100)?.timepoint;
            const submittedOrApproved = isSubmittedOrApproved(formStatus);
            const severityMissing = !['00', '01', '02', '03', '04'].includes(primary?.severity || '');
            if (diagnosisRequiresBlankSeverity(primary?.code, timepoint)) {
                if (!submittedOrApproved && !severityIsBlank(primary?.severity)) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: BLANK_SEVERITY_ISSUE,
                        path: ['SectionI', 'm1021', 'severity'],
                    });
                }
            }
            else if (severityMissing && !(submittedOrApproved && timepoint === 'rct')) {
                // Severity is required for a primary diagnosis that does not start with V–Z.
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: `-4991 Severity required`,
                    path: ['SectionI', 'm1021', 'severity'],
                });
            }
            if (!submittedOrApproved) {
                payload.SectionI?.m1023?.forEach((diagnosis, index) => {
                    if (!diagnosisRequiresBlankSeverity(diagnosis?.code, timepoint) || severityIsBlank(diagnosis?.severity)) {
                        return;
                    }
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: BLANK_SEVERITY_ISSUE,
                        path: ['SectionI', 'm1023', index, 'severity'],
                    });
                });
            }
        }
    }
    else if (!field) {
        // section level
        // eslint-disable-next-line
        const payload = input;
    }
    else {
        // field level
        if (field === 'm1028') {
            const payload = input;
            // I-consistency-5670 (a) If M1028_ACTV_DIAG_NOA = [0], then at least one of M1028_ACTV_DIAG_PVD_PAD and M1028_ACTV_DIAG_DM must equal [1].
            if (payload && !payload.includes('NOA') && !payload.some((item) => ['PVD_PAD', 'DM'].includes(item))) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: `-5670 (a) If None of the above is NOT checked, then at least 1 or 2 must be checked`,
                    path: [],
                });
            }
            // I-consistency-5670 (b) If M1028_ACTV_DIAG_NOA = [1], then M1028_ACTV_DIAG_PVD_PAD and M1028_ACTV_DIAG_DM must equal [0].
            if (payload && payload.includes('NOA') && payload.some((item) => ['PVD_PAD', 'DM'].includes(item))) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: `-5670 (a) If 'None of the above' is checked, then  1 and 2 must NOT be checked`,
                    path: [],
                });
            }
            // I-consistency-5670 (c) If M1028_ACTV_DIAG_NOA = [-], then at least one of M1028_ACTV_DIAG_PVD_PAD and M1028_ACTV_DIAG_DM must equal [-] and the other item must equal [0,-].
            if (payload && payload.includes('-') && !payload.every((item) => item === '-')) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: `-5670 (a) If 'None of the above' is '-', then  1 and 2 must be '-'`,
                    path: [],
                });
            }
        }
    }
};
// I-consistency-5670
// (a) If M1028_ACTV_DIAG_NOA = [0], then at least one of M1028_ACTV_DIAG_PVD_PAD and M1028_ACTV_DIAG_DM must equal [1].
// (b) If M1028_ACTV_DIAG_NOA = [1], then M1028_ACTV_DIAG_PVD_PAD and M1028_ACTV_DIAG_DM must equal [0].
// (c) If M1028_ACTV_DIAG_NOA = [-], then at least one of M1028_ACTV_DIAG_PVD_PAD and M1028_ACTV_DIAG_DM must equal [-] and the other item must equal [0,-].
// I-consistency-5001 If M0100_ASSMT_REASON=[01,03] and if a value submitted in items M1021_PRIMARY_DIAG_ICD, M1023_OTH_DIAG1_ICD, M1023_OTH_DIAG2_ICD, M1023_OTH_DIAG3_ICD, M1023_OTH_DIAG4_ICD, or M1023_OTH_DIAG5_ICD is not equal to [^], it must not equal (must not duplicate) any value submitted in the remaining items in this list.
// I-consistency-5221 If M0100_ASSMT_REASON =[01,03], and if the Primary/Other ICD code in Column 2 is equal to [^], then the corresponding severity code must equal [^].
// I-consistency-5211 If M0100_ASSMT_REASON=[01,03] and if the Primary/Other ICD code in Column 2 is NOT equal to [^] AND character 1 of the ICD code is NOT equal to [V,W,X,Y,Z,v,w,x,y,z], then the corresponding severity code must not equal [^].
// I-consistency-5201 If M0100_ASSMT_REASON=[01,03] and if character 1 of the Primary/Other ICD code in Column 2 is equal to [V,W,X,Y,Z,v,w,x,y,z], then the corresponding severity code must equal [^].
// I-consistency-4991 If M0100_ASSMT_REASON=[01,03], then M1021_PRIMARY_DIAG_SEVERITY should equal [01,02,03,04,^].
